import React, { useState, useEffect } from 'react';
import { updateForecastConfig } from '../../api/analytics';

// Phase 1 supported models only. Phase 2 models shown as disabled.
const MODEL_OPTIONS = {
  binary: [
    { value: 'lgbm',            label: 'LightGBM',              phase: 1, description: 'Gradient boosting — best for large N, non-linear interactions' },
    { value: 'lgbm_calibrated', label: 'Calibrated LightGBM',   phase: 2, description: 'LightGBM + Platt/isotonic calibration — need reliable probabilities' },
    { value: 'logistic',        label: 'Logistic Regression',   phase: 2, description: 'Interpretable, N < 5k, linear decision boundary' },
    { value: 'pymc_logistic',   label: 'Bayesian Logistic',     phase: 2, description: 'PyMC — small N, calibrated uncertainty, weakly informative priors' },
    { value: 'pymc_hierarchical', label: 'Hierarchical Logistic', phase: 2, description: 'PyMC — rep/territory structure, ICC > 0.1' },
  ],
  stage_progression: [
    { value: 'lgbm', label: 'LightGBM', phase: 1, description: 'Gradient boosting classification' },
  ],
  time_to_close: [
    { value: 'lgbm', label: 'LightGBM', phase: 1, description: 'Regression on time-to-close' },
  ],
  multi_class: [
    { value: 'lgbm', label: 'LightGBM', phase: 1, description: 'Multi-class gradient boosting' },
  ],
  survival: [
    { value: 'cox',            label: 'Cox PH',              phase: 1, description: 'Proportional hazards — interpretable hazard ratios (default)' },
    { value: 'stratified_cox', label: 'Stratified Cox',      phase: 1, description: 'PH violated in subgroups — use when log-log KM curves cross' },
    { value: 'km',             label: 'Kaplan-Meier',        phase: 1, description: 'Non-parametric baseline — no covariates, EDA use only' },
    { value: 'weibull_aft',    label: 'Weibull AFT',         phase: 1, description: 'Parametric, extrapolation needed, Weibull shape assumption' },
    { value: 'lognormal_aft',  label: 'Log-normal AFT',      phase: 1, description: 'Long-tail time distributions' },
    { value: 'fine_gray',      label: 'Fine-Gray',           phase: 2, description: 'Competing risks (won vs. lost vs. abandoned) — Phase 2' },
  ],
};

// LightGBM hyperparameters with tier annotations
const LGBM_PARAMS = [
  { key: 'num_leaves',        label: 'Num Leaves',         tier: 1, default: 31,   min: 10,   max: 300,  step: 1,    description: 'Tree complexity — controls overfitting' },
  { key: 'min_child_samples', label: 'Min Child Samples',  tier: 1, default: 20,   min: 5,    max: 200,  step: 1,    description: 'Min rows per leaf — prevents overfitting' },
  { key: 'learning_rate',     label: 'Learning Rate',      tier: 1, default: 0.05, min: 0.005, max: 0.3, step: 0.005, description: 'Step size — lower = better but slower' },
  { key: 'n_estimators',      label: 'Num Trees',          tier: 2, default: 500,  min: 100,  max: 2000, step: 50,   description: 'Number of boosting rounds' },
  { key: 'subsample',         label: 'Row Sample',         tier: 2, default: 0.8,  min: 0.5,  max: 1.0,  step: 0.05, description: 'Fraction of rows per tree' },
  { key: 'colsample_bytree',  label: 'Col Sample',         tier: 2, default: 0.8,  min: 0.5,  max: 1.0,  step: 0.05, description: 'Fraction of features per tree' },
  { key: 'reg_alpha',         label: 'L1 Reg',             tier: 2, default: 0.1,  min: 0,    max: 10,   step: 0.1,  description: 'L1 regularization strength' },
  { key: 'reg_lambda',        label: 'L2 Reg',             tier: 2, default: 1.0,  min: 0,    max: 10,   step: 0.1,  description: 'L2 regularization strength' },
];

// Cox PH has no tunable hyperparameters — strata selection only
const COX_NOTES = 'Cox PH fits automatically. Set strata column in the outcome table config to use Stratified Cox.';

function HyperparamEditor({ modelFamily, hyperparams, onChange }) {
  const [showTier2, setShowTier2] = useState(false);

  if (modelFamily === 'cox' || modelFamily === 'stratified_cox' || modelFamily === 'km' ||
      modelFamily === 'weibull_aft' || modelFamily === 'lognormal_aft') {
    return <p style={{ color: '#666', fontSize: 13 }}>{COX_NOTES}</p>;
  }

  if (!['lgbm', 'lgbm_calibrated'].includes(modelFamily)) {
    return <p style={{ color: '#888', fontSize: 13 }}>Hyperparameter editing available in Phase 2.</p>;
  }

  const tier1 = LGBM_PARAMS.filter(p => p.tier === 1);
  const tier2 = LGBM_PARAMS.filter(p => p.tier === 2);
  const visibleParams = showTier2 ? LGBM_PARAMS : tier1;

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Parameter</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Value</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 400, color: '#888' }}>Range</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 400, color: '#888' }}>Description</th>
          </tr>
        </thead>
        <tbody>
          {visibleParams.map(p => (
            <tr key={p.key} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{p.key}</td>
              <td style={{ padding: '4px 8px' }}>
                <input
                  type="number"
                  min={p.min} max={p.max} step={p.step}
                  value={hyperparams[p.key] ?? p.default}
                  onChange={e => onChange({ ...hyperparams, [p.key]: parseFloat(e.target.value) })}
                  style={{ width: 80, padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc', fontFamily: 'monospace' }}
                  data-testid={`hyperparam-${p.key}`}
                />
              </td>
              <td style={{ padding: '6px 8px', color: '#888', fontSize: 12 }}>{p.min}–{p.max}</td>
              <td style={{ padding: '6px 8px', color: '#666', fontSize: 12 }}>{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        onClick={() => setShowTier2(v => !v)}
        style={{ marginTop: 8, fontSize: 12, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        {showTier2 ? '▲ Hide advanced parameters' : `▼ Show ${tier2.length} advanced parameters`}
      </button>
    </div>
  );
}

function CVConfigEditor({ cvConfig, onChange }) {
  const cfg = cvConfig || { num_folds: 5, horizon_days: 90, gap_days: 0, min_train_days: 730 };
  const update = (k, v) => onChange({ ...cfg, [k]: v });

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
      {[
        { key: 'num_folds', label: 'Folds', min: 2, max: 10 },
        { key: 'horizon_days', label: 'Horizon (days)', min: 1, max: 365 },
        { key: 'gap_days', label: 'Gap (days)', min: 0, max: 180 },
        { key: 'min_train_days', label: 'Min train (days)', min: 30, max: 3650 },
      ].map(({ key, label, min, max }) => (
        <label key={key} style={{ fontSize: 13, color: '#444' }}>
          {label}
          <input
            type="number" min={min} max={max}
            value={cfg[key]}
            onChange={e => update(key, parseInt(e.target.value, 10))}
            style={{ display: 'block', width: 90, marginTop: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
            data-testid={`cv-${key}`}
          />
        </label>
      ))}
    </div>
  );
}

export default function ModelSpecPhase({ configId, config, forecastType: forecastTypeProp, onSpecSaved }) {
  const forecastType = forecastTypeProp || config?.forecast_type || 'binary';
  const existingSpec = config?.model_spec || {};
  const options = MODEL_OPTIONS[forecastType] || MODEL_OPTIONS.binary;

  const [modelFamily, setModelFamily] = useState(existingSpec.model_family || (options.find(o => o.phase === 1)?.value || ''));
  const [hyperparams, setHyperparams] = useState(existingSpec.hyperparams || {});
  const [cvConfig, setCvConfig] = useState(existingSpec.cv_config || null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Reset when config changes externally
  useEffect(() => {
    if (existingSpec.model_family) setModelFamily(existingSpec.model_family);
  }, [config?.model_spec?.model_family]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateForecastConfig(configId, {
        ...config,
        model_spec: { model_family: modelFamily, hyperparams, cv_config: cvConfig },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onSpecSaved) onSpecSaved({ model_family: modelFamily, hyperparams, cv_config: cvConfig });
    } catch (e) {
      setError(e.data?.message || e.message || 'Failed to save model spec');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16 }} data-testid="model-spec-phase">
      {/* Model family selection */}
      <div style={{ marginBottom: 20 }}>
        <strong style={{ display: 'block', marginBottom: 8 }}>Model Family</strong>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {options.map(opt => (
            <div
              key={opt.value}
              onClick={() => opt.phase === 1 && setModelFamily(opt.value)}
              data-testid={`model-option-${opt.value}`}
              style={{
                border: `2px solid ${modelFamily === opt.value ? '#1976d2' : opt.phase === 2 ? '#eee' : '#ccc'}`,
                borderRadius: 8,
                padding: '10px 14px',
                cursor: opt.phase === 1 ? 'pointer' : 'not-allowed',
                opacity: opt.phase === 2 ? 0.5 : 1,
                background: modelFamily === opt.value ? '#e3f2fd' : opt.phase === 2 ? '#fafafa' : '#fff',
                minWidth: 160,
                maxWidth: 220,
                position: 'relative',
              }}
              title={opt.phase === 2 ? 'Phase 2 — not yet available' : opt.description}
            >
              <div style={{ fontWeight: 500, fontSize: 14 }}>{opt.label}</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>{opt.description}</div>
              {opt.phase === 2 && (
                <div style={{ position: 'absolute', top: 6, right: 8, fontSize: 10, color: '#aaa', fontWeight: 600 }}>Phase 2</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Hyperparameters */}
      <div style={{ marginBottom: 20 }}>
        <strong style={{ display: 'block', marginBottom: 8 }}>Hyperparameters</strong>
        <HyperparamEditor modelFamily={modelFamily} hyperparams={hyperparams} onChange={setHyperparams} />
      </div>

      {/* Cross-validation */}
      <div style={{ marginBottom: 20 }}>
        <strong style={{ display: 'block', marginBottom: 4 }}>Temporal Cross-Validation</strong>
        <p style={{ color: '#666', fontSize: 12, margin: '0 0 6px' }}>
          Walk-forward folds only — random CV is disabled to prevent future leakage.
        </p>
        <CVConfigEditor cvConfig={cvConfig} onChange={setCvConfig} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="model-spec-save"
          style={{
            padding: '7px 20px', borderRadius: 4,
            border: '1px solid #1976d2', background: '#1976d2',
            color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: 500,
          }}
        >
          {saving ? 'Saving\u2026' : 'Save Spec'}
        </button>
        {saved && <span style={{ color: '#388e3c', fontSize: 13 }}>\u2713 Saved</span>}
        {error && <span style={{ color: '#d32f2f', fontSize: 13 }}>{error}</span>}
      </div>
    </div>
  );
}
